import type { PolicyExposureViewModel } from '@/api/types/PolicyExposureViewModel';
import type { ClassCodeLookupOption, UpdatePolicyExposureRequest } from 'src/api/services/policy.service';

import { useParams } from 'react-router';
import { memo, useRef, useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Card,
  Table,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  IconButton,
  Typography,
  CardContent,
  Autocomplete,
  TableContainer,
} from '@mui/material';

import { useClassCodeLookups } from 'src/api/hooks/usePolicyQueries';
import { usePolicyExposuresByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';

import { TableNoData } from 'src/components/table';

import { MESSAGES } from 'src/constants/messages';
import { StyledTooltipLabel } from 'src/sections/employer-data/shared/styles/sharedStyles';

import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import { formatDate, formatDollarAmount } from '../../shared/utils/policyUtils';

export type ClassCodeDraftRow = UpdatePolicyExposureRequest & { rowKey: string; policyPeriodId?: number };
type SummaryValue = string | number | undefined;
export type DeletedExposureRef = { policyExposureId: number; policyPeriodId: number };

// Changed-values-only representation of class-code edits, for cross-tab-switch persistence without duplicating unedited read data.
export type ClassCodesDraftDelta = {
  rowPatches?: Record<number, Partial<Omit<ClassCodeDraftRow, 'rowKey' | 'policyExposureId'>>>;
  newRows?: ClassCodeDraftRow[];
  deletedIds?: DeletedExposureRef[];
};

const getClassCodeRowPatch = (
  row: ClassCodeDraftRow,
  previousRowsById: Map<number | undefined, ClassCodeDraftRow>
): { policyExposureId: number; patch: Partial<Omit<ClassCodeDraftRow, 'rowKey' | 'policyExposureId'>> } | null => {
  if (row.rowKey.startsWith('new-')) {
    return null;
  }

  const previousRow = previousRowsById.get(row.policyExposureId);
  if (!previousRow || row.policyExposureId === undefined) {
    return null;
  }

  const patch = { ...row } as Record<string, unknown>;
  const previousValues = { ...previousRow } as Record<string, unknown>;
  delete patch.rowKey;
  delete patch.policyExposureId;
  delete previousValues.rowKey;
  delete previousValues.policyExposureId;
  return JSON.stringify(patch) === JSON.stringify(previousValues)
    ? null
    : { policyExposureId: row.policyExposureId, patch: patch as { [key: string]: unknown } as Partial<Omit<ClassCodeDraftRow, 'rowKey' | 'policyExposureId'>> };
};

export const getClassCodesDraftDelta = (
  snapshotRows: ClassCodeDraftRow[],
  draftRows: ClassCodeDraftRow[],
  deletedIds: DeletedExposureRef[]
): ClassCodesDraftDelta => {
  const previousRowsById = new Map(
    snapshotRows.filter((row) => row.policyExposureId !== undefined).map((row) => [row.policyExposureId, row])
  );
  const rowPatches: NonNullable<ClassCodesDraftDelta['rowPatches']> = {};
  const newRows: ClassCodeDraftRow[] = [];

  draftRows.forEach((row) => {
    if (row.rowKey.startsWith('new-')) {
      newRows.push(row);
      return;
    }

    const rowPatch = getClassCodeRowPatch(row, previousRowsById);
    if (rowPatch) {
      rowPatches[rowPatch.policyExposureId] = rowPatch.patch;
    }
  });

  const delta: ClassCodesDraftDelta = {};
  if (Object.keys(rowPatches).length > 0) delta.rowPatches = rowPatches;
  if (newRows.length > 0) delta.newRows = newRows;
  if (deletedIds.length > 0) delta.deletedIds = deletedIds;
  return delta;
};

export const applyClassCodesDraftDelta = (
  freshRows: ClassCodeDraftRow[],
  delta: ClassCodesDraftDelta | null | undefined
): { rows: ClassCodeDraftRow[]; deletedIds: DeletedExposureRef[] } => {
  if (!delta) {
    return { rows: freshRows, deletedIds: [] };
  }

  const patchedRows = freshRows.map((row) =>
    row.policyExposureId !== undefined && delta.rowPatches?.[row.policyExposureId]
      ? { ...row, ...delta.rowPatches[row.policyExposureId] }
      : row
  );

  return {
    rows: delta.newRows && delta.newRows.length > 0 ? [...patchedRows, ...delta.newRows] : patchedRows,
    deletedIds: delta.deletedIds ?? [],
  };
};

// Standalone validation/summary/payload helpers, callable without the component mounted (e.g. validating/saving
// a cached-but-unmounted tab by reconstructing its draft from view data + cached delta).
export const validateClassCodeRows = (
  rows: ClassCodeDraftRow[],
  policyPeriodEffectiveDate: string | null | undefined,
  classCodeOptions: ClassCodeLookupOption[],
  statisticalCodeOptions: ClassCodeLookupOption[]
): Record<string, string> => {
  const errors: Record<string, string> = {};
  rows.forEach((row) => {
    Object.assign(errors, getRowValidationErrors(row, policyPeriodEffectiveDate, classCodeOptions, statisticalCodeOptions));
  });
  return errors;
};

export const getClassCodesChangedFieldSummaries = (
  snapshotRows: ClassCodeDraftRow[] | null,
  draftRows: ClassCodeDraftRow[],
  classCodeLookups: ClassCodeLookupOption[]
): string[] => {
  if (!snapshotRows) {
    return [];
  }

  const previousRowsById = new Map(
    snapshotRows.filter((row) => row.policyExposureId !== undefined).map((row) => [row.policyExposureId, row])
  );
  const currentRowKeys = new Set(draftRows.map((row) => row.rowKey));
  const summaries: string[] = [];

  snapshotRows
    .filter((row) => row.policyExposureId !== undefined && !currentRowKeys.has(row.rowKey))
    .forEach((row) => {
      const summaryLabel = getClassCodeSummaryLabel(row.exposureActCode);
      const display = getClassCodeDisplay(row.classCode, classCodeLookups);
      if (row.classCode) {
        summaries.push(`${summaryLabel} ${display} removed`);
      }
    });

  draftRows.forEach((row) => {
    const summaryLabel = getClassCodeSummaryLabel(row.exposureActCode);
    const newDisplay = getClassCodeDisplay(row.classCode, classCodeLookups);
    if (row.rowKey.startsWith('new-')) {
      if (row.classCode) {
        summaries.push(`New ${summaryLabel.toLowerCase()} ${newDisplay} added`);
      }
      return;
    }

    const previousRow = previousRowsById.get(row.policyExposureId);
    if (!previousRow) {
      return;
    }

    if (previousRow.classCode !== row.classCode) {
      const previousDisplay = getClassCodeDisplay(previousRow.classCode, classCodeLookups);
      summaries.push(`${summaryLabel} ${previousDisplay} changed to ${newDisplay}`);
    }

    editableSummaryFields.forEach(({ field, label }) => {
      if (previousRow[field] !== row[field]) {
        summaries.push(
          `${label} changed from ${getSummaryValue(previousRow[field] as string | number | undefined)} to ${getSummaryValue(row[field] as string | number | undefined)}`
        );
      }
    });
  });

  return summaries;
};

export const getClassCodesExposuresPayloadByPeriod = (
  snapshotRows: ClassCodeDraftRow[] | null,
  draftRows: ClassCodeDraftRow[],
  policyPeriodId?: number
): Array<{ policyPeriodId: number; exposures: UpdatePolicyExposureRequest[] }> => {
  const previousRowsById = new Map(
    (snapshotRows ?? []).filter((row) => row.policyExposureId !== undefined).map((row) => [row.policyExposureId, row])
  );
  const changedRows = draftRows.filter((row) => {
    if (row.rowKey.startsWith('new-')) {
      return Boolean(row.classCode);
    }

    const previousRow = previousRowsById.get(row.policyExposureId);
    return Boolean(previousRow) && JSON.stringify(previousRow) !== JSON.stringify(row);
  });

  const groups = new Map<number, UpdatePolicyExposureRequest[]>();
  changedRows.forEach(({ rowKey, policyPeriodId: rowPeriodId, ...row }) => {
    const targetPeriodId = rowPeriodId ?? policyPeriodId;
    if (!targetPeriodId) {
      return;
    }

    const payload: UpdatePolicyExposureRequest = {
      ...row,
      policyChangeEffectiveDate: toRequestDate(row.policyChangeEffectiveDate),
      exposurePeriodEffectiveDate: toRequestDate(row.exposurePeriodEffectiveDate),
      exposurePeriodExpirationDate: toRequestDate(row.exposurePeriodExpirationDate),
    };
    groups.set(targetPeriodId, [...(groups.get(targetPeriodId) ?? []), payload]);
  });

  return [...groups.entries()].map(([groupPeriodId, periodExposures]) => ({ policyPeriodId: groupPeriodId, exposures: periodExposures }));
};

export const getClassCodesDeletedPolicyExposureIdsByPeriod = (
  deletedIds: DeletedExposureRef[]
): Array<{ policyPeriodId: number; deletedPolicyExposureIds: number[] }> => {
  const groups = new Map<number, number[]>();
  deletedIds.forEach(({ policyExposureId, policyPeriodId: deletedPeriodId }) => {
    groups.set(deletedPeriodId, [...(groups.get(deletedPeriodId) ?? []), policyExposureId]);
  });
  return [...groups.entries()].map(([groupPeriodId, deletedPolicyExposureIds]) => ({
    policyPeriodId: groupPeriodId,
    deletedPolicyExposureIds,
  }));
};

export type ClassCodesHandle = {
  validateForSave: () => boolean;
  hasPendingChanges: () => boolean;
  getChangedFieldSummaries: () => string[];
  getExposuresPayloadByPeriod: () => Array<{ policyPeriodId: number; exposures: UpdatePolicyExposureRequest[] }>;
  getDeletedPolicyExposureIdsByPeriod: () => Array<{ policyPeriodId: number; deletedPolicyExposureIds: number[] }>;
  commitEdit: () => void;
  cancelEdit: () => void;
};

const toInputDate = (value?: string | null) => (value ? value.slice(0, 10) : '');

const toRequestDate = (value?: string) => {
  if (!value) return undefined;
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};

export const toDraftRow = (exposure: PolicyExposureViewModel, index: number): ClassCodeDraftRow => ({
  rowKey: String(exposure.policyExposureId ?? `existing-${index}`),
  policyExposureId: exposure.policyExposureId,
  policyPeriodId: exposure.policyPeriodId,
  classCode: exposure.classCode ?? exposure.class?.classCode ?? '',
  exposureActCode: exposure.exposureActType?.exposureActCode ?? '01',
  manualRate: exposure.manualRate ?? undefined,
  estimatedExposure: exposure.estimatedExposure ?? undefined,
  estimatedPremium: exposure.estimatedPremium ?? undefined,
  policyChangeEffectiveDate: toInputDate(exposure.policyChangeEffectiveDate),
  exposurePeriodEffectiveDate: toInputDate(exposure.exposurePeriodEffectiveDate),
  exposurePeriodExpirationDate: toInputDate(exposure.exposurePeriodExpirationDate),
});

const getClassCodeDisplay = (classCode: string, lookups: ClassCodeLookupOption[]) => {
  const option = lookups.find((item) => item.classCode === classCode);
  return option ? `${option.classCode} - ${option.classDescription}` : classCode;
};

const getClassCodeSummaryLabel = (exposureActCode?: string) =>
  exposureActCode === '00' ? 'Stat code' : 'Class code';

const getSummaryValue = (value: SummaryValue) =>
  value === undefined || value === '' ? '[blank]' : String(value);

const getRowValidationErrors = (
  row: ClassCodeDraftRow,
  policyPeriodEffectiveDate: string | null | undefined,
  classCodeOptions: ClassCodeLookupOption[],
  statisticalCodeOptions: ClassCodeLookupOption[]
) => {
  const errors: Record<string, string> = {};
  const policyDate = policyPeriodEffectiveDate ? new Date(policyPeriodEffectiveDate) : null;
  const options = row.exposureActCode === '00' ? statisticalCodeOptions : classCodeOptions;
  const option = options.find((item) => item.classCode === row.classCode);

  if (!row.classCode || !option) {
    errors[`${row.rowKey}.classCode`] = 'Select a valid class code.';
  }

  if (option && policyDate) {
    const effectiveDate = new Date(option.classEffectiveDate);
    const expirationDate = option.classExpirationDate ? new Date(option.classExpirationDate) : null;
    if (policyDate < effectiveDate || (expirationDate && policyDate > expirationDate)) {
      errors[`${row.rowKey}.classCode`] = 'The selected class code is not effective for the policy period effective date.';
    }
  }

  if (row.estimatedExposure !== undefined && (!Number.isInteger(row.estimatedExposure) || row.estimatedExposure < 0)) {
    errors[`${row.rowKey}.estimatedExposure`] = 'Enter a non-negative whole number.';
  }
  if (row.estimatedPremium !== undefined && (!Number.isInteger(row.estimatedPremium) || row.estimatedPremium < 0)) {
    errors[`${row.rowKey}.estimatedPremium`] = 'Enter a non-negative whole number.';
  }
  if (row.manualRate !== undefined && (row.manualRate < 0 || row.manualRate > 999999.9999)) {
    errors[`${row.rowKey}.manualRate`] = 'Enter a valid rate.';
  }

  return errors;
};

const editableSummaryFields: Array<{
  field: keyof ClassCodeDraftRow;
  label: string;
}> = [
  { field: 'estimatedExposure', label: 'Estimated payroll' },
  { field: 'manualRate', label: 'Rate per $100' },
  { field: 'estimatedPremium', label: 'Premium' },
  { field: 'policyChangeEffectiveDate', label: 'Effective date' },
  { field: 'exposurePeriodExpirationDate', label: 'Expiration date' },
];

const renderClassLabel = (exposure: PolicyExposureViewModel, testId: string) => (
  <Tooltip title={exposure.class?.classEnum === 'Invalid' ? MESSAGES.CLASS_CODE.INVALID_TOOLTIP : (exposure.class?.description ?? '')} placement="top" data-testid={`${testId}-tooltip-content`}>
    <StyledTooltipLabel data-testid={`${testId}-tooltip`}>
      <Typography component="span" sx={{ typography: 'body2' }}>{exposure.class?.classCode}</Typography>
    </StyledTooltipLabel>
  </Tooltip>
);

function ClassCodesTable({
  dataTestId,
  title,
  columns,
  children,
  empty,
}: Readonly<{
  dataTestId: string;
  title: string;
  columns: string[];
  children: React.ReactNode;
  empty: boolean;
}>) {
  return (
    <Box sx={{ padding: '1.5rem' }}>
      <Typography variant="subtitle1" sx={{ color: 'text.primary' }} data-testid="class-codes-title">
        {title}
      </Typography>
      <TableContainer sx={{ mt: 3, borderBottom: '1px dashed var(--palette-divider)' }}>
        <Table data-testid={dataTestId}>
          <TableHead>
            <TableRow>{columns.map((column) => <TableCell key={column}>{column}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>{empty ? <TableNoData notFound data-testid="class-codes-no-data" /> : children}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const ClassCodes = forwardRef<
  ClassCodesHandle,
  {
    isEditMode?: boolean;
    policyPeriodEffectiveDate?: string | null;
    policyPeriodId?: number;
    initialDraftDelta?: ClassCodesDraftDelta | null;
    onDraftDeltaChange?: (delta: ClassCodesDraftDelta) => void;
  }
>(
  function ClassCodesRenderer(
    { isEditMode = false, policyPeriodEffectiveDate, policyPeriodId, initialDraftDelta = null, onDraftDeltaChange },
    ref
  ) {
    const { comboId, coverageId, policyId } = useParams<{ comboId: string; coverageId: string; policyId: string }>();
    const { data } = usePolicyExposuresByPolicyId(Number(comboId), Number(coverageId), Number(policyId));
    const { data: lookupData } = useClassCodeLookups();
    const headerWidth = useHeaderWidth();
    const exposures = useMemo(() => data?.data ?? [], [data?.data]);
    const classCodeLookups = useMemo(() => lookupData?.data ?? [], [lookupData?.data]);
    const classCodeOptions = useMemo(
      () => classCodeLookups.filter((option) => !option.statCodeIndicator),
      [classCodeLookups]
    );
    const statisticalCodeOptions = useMemo(
      () => classCodeLookups.filter((option) => option.statCodeIndicator),
      [classCodeLookups]
    );
    const initialRows = useMemo(
      () => {
        const sortedExposures = [...exposures].sort((a, b) =>
          (a.class?.classCode ?? '').localeCompare(b.class?.classCode ?? '', undefined, { numeric: true }));
        return sortedExposures.map((exposure, index) => toDraftRow(exposure, index));
      },
      [exposures]
    );
    const [draftRows, setDraftRows] = useState<ClassCodeDraftRow[]>(initialRows);
    const [snapshot, setSnapshot] = useState<ClassCodeDraftRow[] | null>(null);
    const [deletedIds, setDeletedIds] = useState<DeletedExposureRef[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const initialDraftDeltaRef = useRef(initialDraftDelta);
    const hasAppliedInitialDeltaRef = useRef(false);
    // Tracks the last data?.data reference actually processed, so a mount-effect re-invocation with
    // unchanged data (e.g. Strict Mode's double-invoke) is a no-op instead of re-resetting the draft.
    const lastProcessedDataRef = useRef<unknown>(undefined);

    const applyRowValidation = (row: ClassCodeDraftRow) => {
      setFieldErrors((current) => {
        const next = { ...current };
        Object.keys(next)
          .filter((key) => key.startsWith(`${row.rowKey}.`))
          .forEach((key) => {
            delete next[key];
          });

        Object.entries(getRowValidationErrors(row, policyPeriodEffectiveDate, classCodeOptions, statisticalCodeOptions)).forEach(([key, message]) => {
          next[key] = message;
        });

        return next;
      });
    };

    useEffect(() => {
      if (!data?.data || lastProcessedDataRef.current === data.data) {
        return;
      }
      lastProcessedDataRef.current = data.data;
      if (!hasAppliedInitialDeltaRef.current) {
        hasAppliedInitialDeltaRef.current = true;
        const { rows, deletedIds: restoredDeletedIds } = applyClassCodesDraftDelta(initialRows, initialDraftDeltaRef.current);
        setDraftRows(rows);
        setDeletedIds(restoredDeletedIds);
      } else {
        setDraftRows(initialRows);
        setDeletedIds([]);
      }
      setSnapshot(null);
      setFieldErrors({});
    }, [initialRows, data?.data]);

    useEffect(() => {
      if (isEditMode) {
        setSnapshot((current) => current ?? draftRows);
      } else {
        setSnapshot(null);
      }
    }, [draftRows, isEditMode]);

    useEffect(() => {
      if (!hasAppliedInitialDeltaRef.current || !data?.data) {
        return;
      }
      onDraftDeltaChange?.(getClassCodesDraftDelta(snapshot ?? initialRows, draftRows, deletedIds));
    }, [draftRows, snapshot, initialRows, deletedIds, onDraftDeltaChange, data?.data]);

    useImperativeHandle(ref, () => ({
      validateForSave: () => {
        const errors = validateClassCodeRows(draftRows, policyPeriodEffectiveDate, classCodeOptions, statisticalCodeOptions);
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
      },
      hasPendingChanges: () => JSON.stringify(snapshot) !== JSON.stringify(draftRows) || deletedIds.length > 0,
      getChangedFieldSummaries: () => getClassCodesChangedFieldSummaries(snapshot, draftRows, classCodeLookups),
      getExposuresPayloadByPeriod: () => getClassCodesExposuresPayloadByPeriod(snapshot, draftRows, policyPeriodId),
      getDeletedPolicyExposureIdsByPeriod: () => getClassCodesDeletedPolicyExposureIdsByPeriod(deletedIds),
      commitEdit: () => {
        setSnapshot(draftRows);
        setDeletedIds([]);
      },
      cancelEdit: () => {
        if (snapshot) setDraftRows(snapshot);
        setDeletedIds([]);
        setFieldErrors({});
      },
    }), [classCodeLookups, classCodeOptions, deletedIds, draftRows, policyPeriodEffectiveDate, policyPeriodId, snapshot, statisticalCodeOptions]);

    const updateRow = (rowKey: string, update: Partial<ClassCodeDraftRow>) => {
      setDraftRows((rows) => {
        const nextRows = rows.map((row) => (row.rowKey === rowKey ? { ...row, ...update } : row));
        const updatedRow = nextRows.find((row) => row.rowKey === rowKey);
        if (updatedRow) {
          applyRowValidation(updatedRow);
        }
        return nextRows;
      });
    };

    const removeRow = (row: ClassCodeDraftRow) => {
      setDraftRows((rows) => rows.filter((item) => item.rowKey !== row.rowKey));
      Object.keys(fieldErrors)
        .filter((key) => key.startsWith(`${row.rowKey}.`))
        .forEach((key) => {
          setFieldErrors((current) => {
            const next = { ...current };
            delete next[key];
            return next;
          });
        });
      if (row.policyExposureId && row.policyPeriodId) {
        setDeletedIds((ids) => ids.some((id) => id.policyExposureId === row.policyExposureId)
          ? ids
          : [...ids, { policyExposureId: row.policyExposureId!, policyPeriodId: row.policyPeriodId! }]);
      }
    };

    const renderEditRows = (exposureActCode: '00' | '01') => draftRows
      .filter((row) => row.exposureActCode === exposureActCode)
      .map((row) => {
      const options = row.exposureActCode === '00' ? statisticalCodeOptions : classCodeOptions;
      const option = options.find((item) => item.classCode === row.classCode) ?? null;
      return (
        <TableRow key={row.rowKey}>
          <TableCell>
            <Autocomplete<ClassCodeLookupOption>
              sx={{ minWidth: 320 }}
              options={options}
              value={option}
              getOptionLabel={(item) => `${item.classCode} - ${item.classDescription}`}
              isOptionEqualToValue={(left, right) => left.classCode === right.classCode}
              onInputChange={(_, value, reason) => {
                if (reason === 'clear') {
                  updateRow(row.rowKey, { classCode: '' });
                  return;
                }

                if (reason !== 'input') {
                  return;
                }

                const normalizedValue = value.split(' - ')[0]?.trim();
                if (normalizedValue) {
                  updateRow(row.rowKey, { classCode: normalizedValue });
                }
              }}
              onChange={(_, value) => updateRow(row.rowKey, { classCode: value?.classCode ?? '' })}
              renderInput={(params) => <TextField {...params} size="small" error={Boolean(fieldErrors[`${row.rowKey}.classCode`])} helperText={fieldErrors[`${row.rowKey}.classCode`]} data-testid={`class-code-edit-${row.rowKey}`} />}
            />
          </TableCell>
          {(['estimatedExposure', 'manualRate', 'estimatedPremium'] as const).map((field) => (
            <TableCell key={field}>
              <TextField
                fullWidth
                size="small"
                sx={{ maxWidth: 120 }}
                type="number"
                value={row[field] ?? ''}
                error={Boolean(fieldErrors[`${row.rowKey}.${field}`])}
                helperText={fieldErrors[`${row.rowKey}.${field}`]}
                onChange={(event) => updateRow(row.rowKey, { [field]: event.target.value === '' ? undefined : Number(event.target.value) })}
              />
            </TableCell>
          ))}
          <TableCell><TextField size="small" sx={{ width: 145 }} type="date" value={row.policyChangeEffectiveDate ?? ''} onChange={(event) => updateRow(row.rowKey, { policyChangeEffectiveDate: event.target.value })} /></TableCell>
          <TableCell><TextField size="small" sx={{ width: 145 }} type="date" value={row.exposurePeriodExpirationDate ?? ''} onChange={(event) => updateRow(row.rowKey, { exposurePeriodExpirationDate: event.target.value })} /></TableCell>
          <TableCell><IconButton aria-label="Remove class code" onClick={() => removeRow(row)}><DeleteOutlineIcon /></IconButton></TableCell>
        </TableRow>
      );
    });

    const renderViewRows = () => exposures
      .filter((exposure) => exposure.exposureActType?.exposureActCode !== '00')
      .sort((a, b) => (a.class?.classCode ?? '').localeCompare(b.class?.classCode ?? '', undefined, { numeric: true }))
      .map((exp: PolicyExposureViewModel, rowIdx: number) => (
        <TableRow key={exp.policyExposureId ?? rowIdx}>
          <TableCell>{renderClassLabel(exp, `class-codes-row-${rowIdx}-classifications`)}</TableCell>
          <TableCell>{formatDollarAmount(exp.estimatedExposure)}</TableCell>
          <TableCell>{exp.manualRate?.toFixed(3)}</TableCell>
          <TableCell>{formatDollarAmount(exp.estimatedPremium)}</TableCell>
          <TableCell>{formatDate(exp.policyChangeEffectiveDate)}</TableCell>
          <TableCell>{formatDate(exp.exposurePeriodExpirationDate)}</TableCell>
        </TableRow>
      ));

    const renderRows = isEditMode ? renderEditRows('01') : renderViewRows();
    const columns = ['Class code', 'Estimated payroll', 'Rate per $100', 'Premium', 'Effective date', 'Expiration date', ...(isEditMode ? [''] : [])];
    const statisticalExposures = exposures.filter((exposure) => exposure.exposureActType?.exposureActCode === '00');
    const statisticalRows = isEditMode
      ? renderEditRows('00')
      : statisticalExposures.map((exp, index) => (
        <TableRow key={exp.policyExposureId ?? index}>
          <TableCell>{renderClassLabel(exp, `statistical-codes-row-${index}-statistical-code`)}</TableCell>
          <TableCell>{formatDollarAmount(exp.estimatedExposure)}</TableCell>
          <TableCell>{exp.manualRate?.toFixed(3)}</TableCell>
          <TableCell>{formatDollarAmount(exp.estimatedPremium)}</TableCell>
          <TableCell>{formatDate(exp.policyChangeEffectiveDate)}</TableCell>
          <TableCell>{formatDate(exp.exposurePeriodExpirationDate)}</TableCell>
        </TableRow>
      ));

    return (
      <>
        <Card sx={{ display: 'flex', zIndex: 'unset', overflow: 'unset', mt: 5, borderRadius: 2, boxShadow: 'var(--customShadows-card)', width: headerWidth }}>
          <CardContent sx={{ flexGrow: 1, padding: '0px' }} data-testid="policy-class-codes-card">
            <ClassCodesTable dataTestId="class-codes-table" title="Class codes" columns={columns} empty={renderRows.length === 0}>
              {renderRows}
            </ClassCodesTable>
            {isEditMode && <Box sx={{ px: 3, pb: 2 }}><Tooltip title="Add class code"><IconButton aria-label="Add class code" onClick={() => setDraftRows((rows) => [...rows, { rowKey: `new-class-${Date.now()}`, classCode: '', exposureActCode: '01', policyPeriodId }])}><AddIcon /></IconButton></Tooltip></Box>}
          </CardContent>
        </Card>
        <Card sx={{ display: 'flex', zIndex: 'unset', overflow: 'unset', mt: 5, borderRadius: 2, boxShadow: 'var(--customShadows-card)', width: headerWidth }}>
          <CardContent sx={{ flexGrow: 1, padding: '0px' }}>
            <ClassCodesTable dataTestId="statistical-codes-table" title="Statistical codes" columns={['Statistical code', 'Estimated payroll', 'Rate per $100', 'Premium', 'Effective date', 'Expiration date', ...(isEditMode ? [''] : [])]} empty={statisticalRows.length === 0}>
              {statisticalRows}
            </ClassCodesTable>
            {isEditMode && <Box sx={{ px: 3, pb: 2 }}><Tooltip title="Add statistical code"><IconButton aria-label="Add statistical code" onClick={() => setDraftRows((rows) => [...rows, { rowKey: `new-statistical-${Date.now()}`, classCode: '', exposureActCode: '00', policyPeriodId }])}><AddIcon /></IconButton></Tooltip></Box>}
          </CardContent>
        </Card>
      </>
    );
  }
);

ClassCodes.displayName = 'ClassCodes';

export default memo(ClassCodes);
